import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

type SearchParamValue = string | number | null | undefined;

type SearchParamType = 'string' | 'integer';

type ParsedSearchParamValue<T extends SearchParamType> = T extends 'string'
  ? string
  : number;

interface NavigateWithParamOptions {
  /** Optional pathname override for the navigation target. */
  pathname?: string;
  /** Preserve the current scroll position during this navigation. */
  preventScrollReset?: boolean;
  /** Whether the navigation should replace the current history entry. */
  replace?: boolean;
  /** Optional search string override for the navigation target. */
  search?: string;
}

interface BuildPathOptions {
  /** Optional pathname override for the generated URL. */
  pathname?: string;
  /** Optional search string override for the generated URL. */
  search?: string;
}

interface UseURLSearchParamOptions<T extends SearchParamType = 'string'> {
  /** Fallback returned when the raw param is missing or fails parsing. */
  defaultValue?: ParsedSearchParamValue<T> | null;
  /** Parser used for the returned value. */
  type?: T;
}

/**
 * Builds a pathname plus search string while updating one named search param.
 */
export function buildPathWithSearchParam({
  name,
  pathname,
  search = '',
  value,
}: {
  name: string;
  pathname: string;
  search?: string;
  value: SearchParamValue;
}): string {
  const params = new URLSearchParams(search);

  if (value == null || value === '') {
    params.delete(name);
  } else {
    params.set(name, String(value));
  }

  const nextSearch = params.toString();

  return nextSearch ? `${pathname}?${nextSearch}` : pathname;
}

/**
 * Parses a raw search param string into a typed value.
 */
export function parseSearchParamValue<T extends SearchParamType = 'string'>(
  value: string | null | undefined,
  options: UseURLSearchParamOptions<T> = {},
): ParsedSearchParamValue<T> | null {
  const defaultValue = options.defaultValue ?? null;
  const type = options.type ?? ('string' as T);

  if (value == null || value === '') {
    return defaultValue;
  }

  if (type === 'integer') {
    const parsedValue = Number(value);

    return (
      Number.isInteger(parsedValue) ? parsedValue : defaultValue
    ) as ParsedSearchParamValue<T> | null;
  }

  return value as ParsedSearchParamValue<T>;
}

/**
 * Custom hook to read, build, and navigate a single named URL search param.
 */
export function useURLSearchParam<T extends SearchParamType = 'string'>(
  name: string,
  options: UseURLSearchParamOptions<T> = {},
) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const hasParam = searchParams.has(name);
  const rawValue = searchParams.get(name);
  const value = parseSearchParamValue(rawValue, options);

  const buildPath = useCallback(
    (nextValue: SearchParamValue, options: BuildPathOptions = {}) => {
      return buildPathWithSearchParam({
        name,
        value: nextValue,
        pathname: options.pathname ?? location.pathname,
        search: options.search ?? location.search,
      });
    },
    [location.pathname, location.search, name],
  );

  const navigateWithParam = useCallback(
    (nextValue: SearchParamValue, options: NavigateWithParamOptions = {}) => {
      const {
        preventScrollReset = false,
        replace = false,
        pathname,
        search,
      } = options;

      void navigate(buildPath(nextValue, { pathname, search }), {
        preventScrollReset,
        replace,
      });
    },
    [buildPath, navigate],
  );

  return {
    buildPath,
    hasParam,
    navigateWithParam,
    rawValue,
    searchParams,
    value,
  };
}
